
import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Trash2, FileText, Image, Database, Mic } from 'lucide-react';
import DocumentUploader from '@/components/dashboard/agent-builder/DocumentUploader';
import { useToast } from '@/hooks/use-toast';

interface ArtifactsStepProps {
  agentData: any;
  setAgentData: (data: any) => void;
  userId: string;
}

interface Artifact {
  id: string;
  name: string;
  type: string;
  file?: File;
}

interface LogicalFunction {
  id: string;
  name: string;
  description: string;
  artifactIds: string[];
}

const ArtifactsStep: React.FC<ArtifactsStepProps> = ({ agentData, setAgentData, userId }) => {
  const { toast } = useToast();
  const [artifacts, setArtifacts] = useState<Artifact[]>(agentData.artifacts || []);
  const [functions, setFunctions] = useState<LogicalFunction[]>(agentData.functions || []);
  const [currentArtifact, setCurrentArtifact] = useState<Artifact>({ id: '', name: '', type: 'document' });
  const [currentFunction, setCurrentFunction] = useState<LogicalFunction>({ id: '', name: '', description: '', artifactIds: [] });
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const addArtifact = () => {
    if (!currentArtifact.name) {
      toast({
        title: "Error",
        description: "Artifact name is required",
        variant: "destructive"
      });
      return;
    }

    const newArtifact = {
      ...currentArtifact,
      id: `artifact-${Date.now()}`
    };

    const updatedArtifacts = [...artifacts, newArtifact];
    setArtifacts(updatedArtifacts);
    setAgentData({
      ...agentData,
      artifacts: updatedArtifacts
    });
    setCurrentArtifact({ id: '', name: '', type: 'document' });
    setUploadedFiles([]);

    toast({
      title: "Artifact added",
      description: `${newArtifact.name} has been added to your artifacts list`
    });
  };

  const removeArtifact = (id: string) => {
    const updatedArtifacts = artifacts.filter(a => a.id !== id);
    setArtifacts(updatedArtifacts);
    
    // Also remove this artifact from any functions that use it
    const updatedFunctions = functions.map(f => ({
      ...f,
      artifactIds: f.artifactIds.filter(artifactId => artifactId !== id)
    }));
    
    setFunctions(updatedFunctions);
    setAgentData({
      ...agentData,
      artifacts: updatedArtifacts,
      functions: updatedFunctions
    });
  };

  const addFunction = () => {
    if (!currentFunction.name) {
      toast({
        title: "Error",
        description: "Function name is required",
        variant: "destructive"
      });
      return;
    }

    const newFunction = {
      ...currentFunction,
      id: `function-${Date.now()}`
    };

    const updatedFunctions = [...functions, newFunction];
    setFunctions(updatedFunctions);
    setAgentData({
      ...agentData,
      functions: updatedFunctions
    });
    setCurrentFunction({ id: '', name: '', description: '', artifactIds: [] });

    toast({
      title: "Logical function added",
      description: `${newFunction.name} has been added to your functions list`
    });
  };

  const removeFunction = (id: string) => {
    const updatedFunctions = functions.filter(f => f.id !== id);
    setFunctions(updatedFunctions);
    setAgentData({
      ...agentData,
      functions: updatedFunctions
    });
  };

  const handleFileUpload = (files: File[]) => {
    if (files.length > 0) {
      setUploadedFiles(files);
      setCurrentArtifact({
        ...currentArtifact,
        file: files[0]
      });
    }
  };

  const getArtifactIcon = (type: string) => {
    switch (type) {
      case 'document':
        return <FileText className="h-4 w-4" />;
      case 'image':
        return <Image className="h-4 w-4" />;
      case 'api':
        return <Database className="h-4 w-4" />;
      case 'audio':
        return <Mic className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Extracted Logical Functions</h3>
        
        {agentData.sopDocument ? (
          <div className="bg-blue-50 p-4 rounded-md mb-4">
            <p className="text-blue-800 text-sm">
              <strong>Document Analysis:</strong> The SOP document "{agentData.sopDocument.name}" has been analyzed.
            </p>
            <p className="text-blue-700 text-xs mt-1">
              You can now define artifacts and associate them with the extracted logical functions.
            </p>
          </div>
        ) : (
          <p className="text-amber-600 text-sm">
            No SOP document has been analyzed yet. You can still manually define logical functions.
          </p>
        )}

        <div className="border rounded-md p-4">
          <div className="space-y-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="functionName">Function Name</Label>
                <Input
                  id="functionName"
                  placeholder="e.g., Verify Customer Identity"
                  value={currentFunction.name}
                  onChange={(e) => setCurrentFunction({ ...currentFunction, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="functionArtifacts">Associated Artifacts</Label>
                <Select 
                  value={currentFunction.artifactIds.length > 0 ? currentFunction.artifactIds[0] : ''}
                  onValueChange={(value) => {
                    const updatedArtifactIds = currentFunction.artifactIds.includes(value)
                      ? currentFunction.artifactIds.filter(id => id !== value)
                      : [...currentFunction.artifactIds, value];
                    setCurrentFunction({ ...currentFunction, artifactIds: updatedArtifactIds });
                  }}
                >
                  <SelectTrigger id="functionArtifacts" className="mt-1">
                    <SelectValue placeholder="Select artifacts" />
                  </SelectTrigger>
                  <SelectContent>
                    {artifacts.map(artifact => (
                      <SelectItem key={artifact.id} value={artifact.id}>
                        {artifact.name} ({artifact.type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="functionDescription">Function Description</Label>
              <Textarea
                id="functionDescription"
                placeholder="Describe what this function does"
                value={currentFunction.description}
                onChange={(e) => setCurrentFunction({ ...currentFunction, description: e.target.value })}
                className="mt-1 min-h-[80px]"
              />
            </div>

            <Button
              onClick={addFunction}
              className="w-full bg-[#0EA5E9] text-white hover:bg-[#0EA5E9]/90"
            >
              <Plus className="h-4 w-4 mr-2" /> Add Logical Function
            </Button>
          </div>

          {functions.length > 0 && (
            <div className="space-y-3 mt-4">
              <Label>Defined Logical Functions:</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {functions.map(func => (
                  <Card key={func.id} className="bg-gray-50">
                    <CardContent className="p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">{func.name}</h4>
                          <p className="text-xs text-gray-600 mt-1">{func.description}</p>
                          
                          {func.artifactIds.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs font-medium text-gray-700">Artifacts:</p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {func.artifactIds.map(id => {
                                  const artifact = artifacts.find(a => a.id === id);
                                  return artifact ? (
                                    <span key={id} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex items-center">
                                      {getArtifactIcon(artifact.type)}
                                      <span className="ml-1">{artifact.name}</span>
                                    </span>
                                  ) : null;
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFunction(func.id)}
                          className="h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-4 mt-8">
        <h3 className="text-lg font-medium">Define Artifacts</h3>
        <p className="text-sm text-gray-600">
          Define what inputs (documents, images, API calls, audio) are needed for each logical function.
        </p>

        <div className="border rounded-md p-4">
          <div className="space-y-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="artifactName">Artifact Name</Label>
                <Input
                  id="artifactName"
                  placeholder="e.g., Customer ID Document"
                  value={currentArtifact.name}
                  onChange={(e) => setCurrentArtifact({ ...currentArtifact, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="artifactType">Artifact Type</Label>
                <Select 
                  value={currentArtifact.type} 
                  onValueChange={(value) => setCurrentArtifact({ ...currentArtifact, type: value })}
                >
                  <SelectTrigger id="artifactType" className="mt-1">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="document">Document</SelectItem>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="api">API Call</SelectItem>
                    <SelectItem value="audio">Audio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {(currentArtifact.type === 'document' || currentArtifact.type === 'image') && (
              <div>
                <Label>Upload Sample Artifact</Label>
                <DocumentUploader
                  uploadedDocuments={uploadedFiles}
                  setUploadedDocuments={handleFileUpload}
                />
              </div>
            )}

            {currentArtifact.type === 'api' && (
              <div>
                <Label htmlFor="apiEndpoint">API Endpoint</Label>
                <Input
                  id="apiEndpoint"
                  placeholder="https://api.example.com/endpoint"
                  className="mt-1"
                />
              </div>
            )}

            <Button
              onClick={addArtifact}
              className="w-full bg-[#0EA5E9] text-white hover:bg-[#0EA5E9]/90"
            >
              <Plus className="h-4 w-4 mr-2" /> Add Artifact
            </Button>
          </div>

          {artifacts.length > 0 && (
            <div className="space-y-3 mt-4">
              <Label>Defined Artifacts:</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {artifacts.map(artifact => (
                  <Card key={artifact.id} className="bg-gray-50">
                    <CardContent className="p-3">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          {getArtifactIcon(artifact.type)}
                          <div className="ml-2">
                            <h4 className="font-medium text-sm">{artifact.name}</h4>
                            <p className="text-xs text-gray-600">{artifact.type}</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeArtifact(artifact.id)}
                          className="h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArtifactsStep;
